/**
 * GunfighterOS Bridge Sync Module
 * Handles reading/writing bridge snapshots between OPMODE and GunfighterOS
 * via Firestore, with local Dexie caching for offline access.
 */

import { db } from './db';
import {
  OpmodeBridgeSnapshot,
  GunfighterBridgeSnapshot,
  OpmodeBridgeRecord,
  GunfighterBridgeRecord,
  isBridgeStale,
} from './tactiv-bridge';
import { loadProgressionState } from './progressionService';

// Lazy-load Firestore to avoid Firebase initialization during SSR/static export
// OPMODE's Firestore (for publishing our data)
async function getOpmodeFirestore() {
  const { db: firestoreDb } = await import('./firebase');
  const { doc, getDoc, setDoc } = await import('firebase/firestore');
  return { firestoreDb, doc, getDoc, setDoc };
}

// GunfighterOS Firestore (for reading GunfighterOS bridge data - separate Firebase project)
async function getGunfighterFirestoreDb() {
  const { getGunfighterFirestore } = await import('./gunfighterFirebase');
  const { doc, getDoc } = await import('firebase/firestore');
  const db = getGunfighterFirestore();
  return { db, doc, getDoc };
}

// ============================================================================
// READ GUNFIGHTER BRIDGE (cached from Dexie)
// ============================================================================

export async function getGunfighterBridge(userId: string): Promise<GunfighterBridgeRecord | null> {
  const record = await db.gunfighterBridge.get(userId);
  return record ?? null;
}

export async function getGunfighterBridgeSnapshot(userId: string): Promise<GunfighterBridgeSnapshot | null> {
  const record = await getGunfighterBridge(userId);
  return record?.snapshot ?? null;
}

// ============================================================================
// SYNC GUNFIGHTER BRIDGE (Firestore -> Dexie)
// ============================================================================

export async function syncGunfighterBridge(userId: string): Promise<GunfighterBridgeSnapshot | null> {
  try {
    // Get the linked GunfighterOS userId (different Firebase project = different UID)
    const progressionState = await loadProgressionState(userId);
    const gunfighterUserId = progressionState.gunfighterUserId;

    if (!gunfighterUserId) {
      console.log('No GunfighterOS account linked - skipping sync');
      return null;
    }

    // Read from GunfighterOS's Firestore (separate Firebase project)
    const { db: gunfighterDb, doc, getDoc } = await getGunfighterFirestoreDb();
    const docRef = doc(gunfighterDb, 'bridges', gunfighterUserId, 'data', 'gunfighter');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const snapshot = docSnap.data() as GunfighterBridgeSnapshot;

    // Cache to Dexie
    const record: GunfighterBridgeRecord = {
      userId,
      snapshot,
      snapshotAt: snapshot.snapshotAt,
      cachedAt: new Date().toISOString(),
    };

    await db.gunfighterBridge.put(record);

    return snapshot;
  } catch (error) {
    console.warn('Failed to sync GunfighterOS bridge, using cached data:', error);
    // Fall back to cached
    const cached = await getGunfighterBridgeSnapshot(userId);
    return cached;
  }
}

// ============================================================================
// PUBLISH OPMODE SNAPSHOT (Dexie + Firestore)
// ============================================================================

export async function publishOpmodeSnapshot(userId: string): Promise<void> {
  try {
    // Gather OPMODE data
    const progressionState = await loadProgressionState(userId);

    // Get recovery data
    let readinessData: OpmodeBridgeSnapshot['readiness'] = null;
    try {
      const { getRecoveryHistory } = await import('./recoveryCalculator');
      const history = await getRecoveryHistory();
      if (history.length > 0) {
        const latest = history[history.length - 1];
        const score = latest.readinessScore || 70;
        readinessData = {
          score,
          level: score >= 70 ? 'green' : score >= 50 ? 'amber' : 'red',
          sleepHours: latest.sleepHours || 7,
          soreness: latest.overallSoreness || 5,
          energy: latest.energy || 5,
        };
      }
    } catch (error) {
      console.warn('Failed to get recovery data for snapshot:', error);
    }

    // Get recovery debt/ACWR data
    let recoveryDebtData: OpmodeBridgeSnapshot['recoveryDebt'] = null;
    try {
      const { calculateEnhancedACWR } = await import('./enhancedACWR');
      const acwrAnalysis = await calculateEnhancedACWR();
      const acwr = acwrAnalysis.overallACWR;

      let debtLevel: 'low' | 'moderate' | 'high' | 'severe' = 'low';
      if (acwr > 1.5) debtLevel = 'severe';
      else if (acwr > 1.3) debtLevel = 'high';
      else if (acwr > 1.1) debtLevel = 'moderate';

      recoveryDebtData = {
        overallDebt: Math.min(100, Math.round(acwr * 50)),
        debtLevel,
        acwr,
        needsDeload: acwr > 1.3,
      };
    } catch (error) {
      console.warn('Failed to get ACWR data for snapshot:', error);
    }

    // Calculate operator score using the REAL unified intelligence engine
    let operatorScoreData: OpmodeBridgeSnapshot['operatorScore'] = null;
    try {
      const { createIntelligenceEngine } = await import('./unifiedIntelligence');
      const engine = await createIntelligenceEngine();
      const report = await engine.analyze();

      const score = report.currentState.operatorScore;
      const breakdown = report.currentState.operatorScoreBreakdown;

      let level = 'beginner';
      if (score >= 80) level = 'elite';
      else if (score >= 60) level = 'advanced';
      else if (score >= 40) level = 'proficient';
      else if (score >= 20) level = 'developing';

      // Normalize breakdown to 0-100 scale (they're already on their max scale)
      // Strength max=30, Endurance max=25, Bodyweight max=20, Recovery max=15, Consistency max=10
      operatorScoreData = {
        score,
        level,
        breakdown: {
          strength: Math.round((breakdown.strength / 30) * 100),
          endurance: Math.round((breakdown.endurance / 25) * 100),
          bodyweight: Math.round((breakdown.bodyweight / 20) * 100),
          recovery: Math.round((breakdown.recovery / 15) * 100),
          consistency: Math.round((breakdown.consistency / 10) * 100),
        },
      };
    } catch (error) {
      console.warn('Failed to calculate operator score for snapshot:', error);
    }

    // Get this week's session count
    let workoutsThisWeek = 0;
    try {
      const { getThisWeekSessions } = await import('./db');
      const weekSessions = await getThisWeekSessions(userId);
      workoutsThisWeek = weekSessions.length;
    } catch (error) {
      console.warn('Failed to get this week sessions:', error);
    }

    // Get bodyweight from user profile
    let bodyweight: number | null = null;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db: firestoreDb } = await import('./firebase');
      const userDoc = await getDoc(doc(firestoreDb, 'users', userId));
      if (userDoc.exists()) {
        bodyweight = userDoc.data()?.weight || null;
      }
    } catch (error) {
      console.warn('Failed to get bodyweight:', error);
    }

    const snapshot: OpmodeBridgeSnapshot = {
      schemaVersion: 1,
      userId,
      snapshotAt: new Date().toISOString(),

      readiness: readinessData,
      recoveryDebt: recoveryDebtData,
      operatorScore: operatorScoreData,

      streakDays: progressionState.streakDays || 0,
      workoutsThisWeek,
      bodyweight,
      opmodeArchetype: progressionState.equippedArchetype || null,
    };

    // Save to local cache
    const record: OpmodeBridgeRecord = {
      userId,
      snapshot,
      snapshotAt: snapshot.snapshotAt,
      cachedAt: new Date().toISOString(),
    };

    await db.opmodeBridge.put(record);

    // Publish to Firestore
    try {
      const { firestoreDb, doc, setDoc } = await getOpmodeFirestore();
      const docRef = doc(firestoreDb, 'bridges', userId, 'data', 'opmode');
      await setDoc(docRef, snapshot);
      console.log(' OPMODE snapshot published to Firestore');
    } catch (error) {
      console.warn('Failed to publish OPMODE bridge to Firestore:', error);
    }
  } catch (error) {
    console.error('Failed to publish OPMODE snapshot:', error);
  }
}

// ============================================================================
// BRIDGE STATUS HELPERS
// ============================================================================

export interface BridgeStatus {
  isLinked: boolean;
  hasData: boolean;
  isStale: boolean;
  lastSyncAt: string | null;
  snapshot: GunfighterBridgeSnapshot | null;
}

export async function getGunfighterBridgeStatus(userId: string): Promise<BridgeStatus> {
  // Check if user has a linked GunfighterOS account
  const progressionState = await loadProgressionState(userId);

  if (!progressionState.gunfighterUserId) {
    return { isLinked: false, hasData: false, isStale: false, lastSyncAt: null, snapshot: null };
  }

  const record = await getGunfighterBridge(userId);

  if (!record) {
    return { isLinked: true, hasData: false, isStale: false, lastSyncAt: null, snapshot: null };
  }

  return {
    isLinked: true,
    hasData: true,
    isStale: isBridgeStale(record.snapshotAt),
    lastSyncAt: record.cachedAt,
    snapshot: record.snapshot,
  };
}

// ============================================================================
// LINK STATUS CHECK
// ============================================================================

export async function isGunfighterLinked(userId: string): Promise<boolean> {
  // First check local Dexie state
  const progressionState = await loadProgressionState(userId);

  if (progressionState.gunfighterUserId) {
    return true;
  }

  // If not linked locally, check Firestore (might not have synced yet)
  try {
    const { firestoreDb, doc, getDoc } = await getOpmodeFirestore();
    const progressionRef = doc(firestoreDb, `users/${userId}/progression`, 'current');
    const snapshot = await getDoc(progressionRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      if (data?.gunfighterUserId) {
        // Sync the link data to local storage
        progressionState.gunfighterUserId = data.gunfighterUserId;
        progressionState.gunfighterLinkedAt = data.gunfighterLinkedAt;
        progressionState.gunfighterLastSync = data.gunfighterLastSync;

        const { saveProgressionState } = await import('./progressionService');
        await saveProgressionState(progressionState);

        console.log('✓ GunfighterOS link synced from Firestore');
        return true;
      }
    }
  } catch (error) {
    console.warn('Could not check Firestore for link status:', error);
  }

  return false;
}

export async function linkGunfighterAccount(userId: string, gunfighterUserId: string): Promise<void> {
  const { saveProgressionState } = await import('./progressionService');
  const state = await loadProgressionState(userId);

  // GunfighterOS is a separate Firebase project with different UIDs
  // The user must provide their GunfighterOS UID to link accounts
  if (!gunfighterUserId) {
    throw new Error('GunfighterOS User ID is required to link accounts');
  }
  state.gunfighterUserId = gunfighterUserId;
  state.gunfighterLinkedAt = new Date().toISOString();

  await saveProgressionState(state);

  // Publish initial snapshot
  await publishOpmodeSnapshot(userId);

  // Sync GunfighterOS data
  await syncGunfighterBridge(userId);

  console.log(' GunfighterOS account linked');
}

export async function unlinkGunfighterAccount(userId: string): Promise<void> {
  const { saveProgressionState } = await import('./progressionService');
  const state = await loadProgressionState(userId);

  state.gunfighterUserId = undefined;
  state.gunfighterLinkedAt = undefined;
  state.gunfighterLastSync = undefined;

  await saveProgressionState(state);

  console.log(' GunfighterOS account unlinked (cosmetics preserved)');
}
